import { useIsFetching } from '@tanstack/react-query';
import {
  useStaffGetMonthEndReport,
  useInvalidateUseStaffGetMonthEndReport,
} from '@urbanx/agx.rex.client';
import {
  AgxButton,
  AgxDatePicker,
  AgxSpacer,
  AgxTypography,
  dateOnlyToDate,
  toDateMediumString,
  toDateOnlyString,
  toExclusiveDate,
  toInclusiveDate,
} from '@urbanx/agx.ui-components2';
import CleanPanel from 'components/cleanPanel/CleanPanel';
import { downloadBlob } from 'helpers/fileDownload';
import { useState } from 'react';
import { monthEndCsv, monthEndFilename } from './monthEndCsv';
import { MonthEndSummary } from './MonthEndSummary';
import * as styles from './MonthEndReport.css';

const getMonthPeriod = (month: string) => {
  if (!month) return undefined;
  try {
    const fromDate = `${month}-01`;
    const date = dateOnlyToDate(fromDate);
    if (!date) return undefined;
    const toDateExclusive = toDateOnlyString(new Date(date.getFullYear(), date.getMonth() + 1, 1));
    if (toDateExclusive?.length !== 10) return undefined;
    return { fromDate, toDateExclusive };
  } catch {
    // Native month inputs emit draft years such as 0002 while the user is typing.
    return undefined;
  }
};

export const MonthEndReportView = () => {
  const [mode, setMode] = useState('default');
  const [month, setMonth] = useState('');
  const [from, setFrom] = useState<string>();
  const [toInclusive, setToInclusive] = useState<string>();
  const [invalidFrom, setInvalidFrom] = useState(false);
  const [invalidTo, setInvalidTo] = useState(false);
  const monthPeriod = getMonthPeriod(month);
  const monthStart = monthPeriod?.fromDate;
  const nextMonth = monthPeriod?.toDateExclusive;
  const fromDate = mode === 'month' ? monthStart : mode === 'custom' ? from : undefined;
  const toDateExclusive =
    mode === 'month'
      ? nextMonth
      : mode === 'custom' && toInclusive
        ? toExclusiveDate(toInclusive)
        : undefined;
  const valid =
    mode === 'default' ||
    (!!fromDate && !!toDateExclusive && fromDate < toDateExclusive && !invalidFrom && !invalidTo);
  const { data, isPending, isError } = useStaffGetMonthEndReport(fromDate, toDateExclusive, valid);
  const invalidate = useInvalidateUseStaffGetMonthEndReport();
  const fetching =
    useIsFetching({
      queryKey: ['staffGetMonthEndReport', fromDate, toDateExclusive],
      exact: true,
    }) > 0;
  const report = valid && !isError && !isPending && !fetching ? data : undefined;
  const error = !valid ? 'Choose a complete date range with From on or before To.' : undefined;

  return (
    <CleanPanel>
      <AgxSpacer direction="vertical" gap="spacing-2xl" padding="spacing-2xl" fill>
        <AgxTypography type="heading" size="medium">
          Month-end report
        </AgxTypography>
        <AgxSpacer direction="horizontal" gap="spacing-xl" wrap fill>
          <label className={styles.field}>
            Period
            <select
              className={styles.control}
              aria-label="Period"
              value={mode}
              onChange={(event) => {
                setMode(event.target.value);
                setInvalidFrom(false);
                setInvalidTo(false);
              }}
            >
              <option value="default">Previous completed month</option>
              <option value="month">Another month</option>
              <option value="custom">Custom range</option>
            </select>
          </label>
          {mode === 'month' && (
            <label className={styles.field}>
              Month
              <input
                className={styles.control}
                type="month"
                aria-label="Month"
                value={month}
                onChange={(event) => setMonth(event.target.value)}
                aria-invalid={!valid}
                aria-describedby={error ? 'month-end-period-error' : undefined}
              />
            </label>
          )}
          {mode === 'custom' && (
            <>
              <AgxDatePicker
                id="month-end-from"
                label="From"
                value={from}
                optionalText=""
                disableValidation
                ariaDescribedBy={error ? 'month-end-period-error' : undefined}
                hasError={!!error}
                onInvalidValue={() => setInvalidFrom(true)}
                onChange={(value) => {
                  setInvalidFrom(false);
                  setFrom(toDateOnlyString(value));
                }}
              />
              <AgxDatePicker
                id="month-end-to"
                label="To (inclusive)"
                value={toInclusive}
                optionalText=""
                disableValidation
                ariaDescribedBy={error ? 'month-end-period-error' : undefined}
                hasError={!!error}
                onInvalidValue={() => setInvalidTo(true)}
                onChange={(value) => {
                  setInvalidTo(false);
                  setToInclusive(toDateOnlyString(value));
                }}
              />
            </>
          )}
          <AgxButton
            id="month-end-export"
            text="Export CSV"
            variant="hollow"
            disabled={!report}
            onClick={() => {
              if (report)
                downloadBlob(
                  new Blob([monthEndCsv(report)], { type: 'text/csv;charset=utf-8' }),
                  monthEndFilename(report)
                );
            }}
          />
        </AgxSpacer>
        {error && (
          <p id="month-end-period-error" role="alert">
            {error}
          </p>
        )}
        {valid && (isPending || fetching) && <p role="status">Loading report…</p>}
        {valid && isError && (
          <div role="alert">
            Unable to load the report.
            <AgxButton
              id="month-end-retry"
              text="Retry"
              variant="hollow"
              onClick={() => {
                void invalidate(fromDate, toDateExclusive);
              }}
            />
          </div>
        )}
        {report && (
          <>
            <AgxTypography type="title" size="large">
              {toDateMediumString(report.fromDate)} –{' '}
              {toDateMediumString(toInclusiveDate(report.toDateExclusive))}
            </AgxTypography>
            <AgxTypography type="body" size="small">
              Figures use current reporting data, team membership and agency tiers. Plus agencies
              are included in their base tier. Listings currently marked Sold are excluded,
              including when viewing earlier periods. Awards and rankings use unconditional
              contracts; listing awards use listing-created dates. Equal results share a place,
              including every tie at the Top Ten cutoff.
            </AgxTypography>
            <MonthEndSummary report={report} />
          </>
        )}
      </AgxSpacer>
    </CleanPanel>
  );
};
